/**
 * Quote Database Persistence Adapter
 * 
 * Database persistence adapter for the Quote Wizard that saves drafts
 * to the backend database via the quote service API.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

import type { WizardDraft } from '../../../framework/wizard/stores/useWizardPersistence';
import type { DatabasePersistenceService } from '../../../framework/wizard/adapters/HybridPersistenceAdapter';
import type { QuoteWizardData } from '../QuoteWizard';
import { quoteService } from '../../../services/quoteService';
import { ApiError } from '../../../utils/errorHandling';
import type { CreateDraftQuoteRequest, UpdateQuoteRequest } from '../../../services/quoteService';

export class QuoteDatabaseAdapter implements DatabasePersistenceService<QuoteWizardData> {
  async saveDraft(draft: Omit<WizardDraft<QuoteWizardData>, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    
    
    try {
      // Convert wizard draft to quote draft format

      // Save via quote service - implement real draft saving
      if (draft.data.quote?.id) {
        // Update existing quote as draft
        const draftUpdateData = this.convertWizardDataToQuoteUpdate(draft.data);
        await quoteService.updateQuote(draft.data.quote.id, draftUpdateData);
        return draft.data.quote.id; // Return the quote ID as draft ID
      } else if (draft.data.quote?.productId) {
        // Create new draft quote if we have enough data
        const draftCreateData = this.convertWizardDataToQuoteCreate(draft.data);
        const response = await quoteService.createDraftQuote(draftCreateData);
        
        // Important: Update the wizard data with the new quote
        // This allows subsequent saves to work as updates
        draft.data.quote = response.quote;
        
        return response.quote.id;
      } else {
        // Not enough data to create a quote yet, just save to localStorage
        throw new Error('Not enough data to save to database yet. Please select a product first.');
      }
    } catch (error) {
      console.error('Failed to save quote draft to database:', error);
      
      if (error instanceof ApiError) {
        throw new Error(`Database save failed: ${error.message}`);
      } else {
        throw new Error('Failed to save draft to database');
      }
    }
  }

  async loadDraft(draftId: string): Promise<WizardDraft<QuoteWizardData> | null> {
    try {
      // Load quote by ID and convert to wizard draft format
      const response = await quoteService.getQuoteById(draftId);
      const quote = response.quote;
      
      if (!quote || quote.state !== 'new') {
        return null;
      }

      // Convert quote back to wizard data format
      const wizardData = this.convertQuoteToWizardData(quote);

      // Convert quote to wizard draft format
      const wizardDraft: WizardDraft<QuoteWizardData> = {
        id: quote.id,
        wizardId: 'quote-wizard',
        data: wizardData,
        createdAt: new Date(quote.createdAt),
        updatedAt: new Date(quote.updatedAt),
        metadata: {
          currentStepId: 'product', // Default to first step
          version: '1.0.0',
        },
      };

      return wizardDraft;
    } catch (error) {
      console.error('Failed to load quote draft from database:', error);
      
      if (error instanceof ApiError && error.statusCode === 404) {
        return null; // Draft not found
      }
      
      throw new Error('Failed to load draft from database');
    }
  }

  async deleteDraft(draftId: string): Promise<boolean> {
    try {
      await quoteService.deleteDraftQuote(draftId);
      return true;
    } catch (error) {
      console.error('Failed to delete quote draft from database:', error);
      
      if (error instanceof ApiError && error.statusCode === 404) {
        return true; // Already deleted
      }
      
      return false;
    }
  }

  async listDrafts(wizardId: string): Promise<WizardDraft<QuoteWizardData>[]> {
    try {
      // Get all draft quotes for the current user
      const response = await quoteService.getQuotes(1, { status: 'DRAFT' }, 50, false); // Use status for API compatibility
      const draftQuotes = response.quotes;

      // Convert quote drafts to wizard drafts
      return draftQuotes.map(quote => ({
        id: quote.id,
        wizardId: wizardId,
        data: this.convertQuoteToWizardData(quote),
        createdAt: new Date(quote.createdAt),
        updatedAt: new Date(quote.updatedAt),
        metadata: {
          currentStepId: 'product', // Default to first step
          version: '1.0.0',
        },
      }));
    } catch (error) {
      console.error('Failed to list quote drafts from database:', error);
      
      if (error instanceof ApiError) {
        throw new Error(`Database list failed: ${error.message}`);
      } else {
        throw new Error('Failed to list drafts from database');
      }
    }
  }

  /**
   * Sync a draft from localStorage to database
   */
  async syncDraft(localDraft: WizardDraft<QuoteWizardData>): Promise<void> {
    try {
      // Sync by saving the draft - this will create or update as needed
      await this.saveDraft({
        wizardId: localDraft.wizardId,
        data: localDraft.data,
        metadata: {
          ...localDraft.metadata,
          // syncedFromLocal: true, // Not part of the interface
          // syncTimestamp: new Date().toISOString(),
        } as any,
      });
    } catch (error) {
      console.error('Failed to sync draft to database:', error);
      throw new Error('Sync to database failed');
    }
  }

  /**
   * Clean up old drafts (called periodically)
   */
  async cleanupOldDrafts(wizardId: string, keepCount = 10): Promise<void> {
    try {
      // Get all drafts, sorted by updatedAt descending
      const drafts = await this.listDrafts(wizardId);
      
      // Keep only the most recent drafts
      const draftsToDelete = drafts.slice(keepCount);
      
      // Delete old drafts
      for (const draft of draftsToDelete) {
        try {
          await this.deleteDraft(draft.id);
        } catch (error) {
          console.warn(`Failed to delete old draft ${draft.id}:`, error);
          // Continue with other deletions
        }
      }
    } catch (error) {
      console.error('Failed to cleanup old drafts:', error);
      // Don't throw - this is a background operation
    }
  }

  /**
   * Convert wizard data to quote update format (for existing quotes)
   */
  private convertWizardDataToQuoteUpdate(data: QuoteWizardData): UpdateQuoteRequest {
    const updateData: Record<string, unknown> = {
      templateName: data.quote?.templateName || 'Draft Quote',
      status: 'DRAFT' as const,
      productId: data.quote?.productId,
      isMasterPolicy: data.quote?.isMasterPolicy || false,
      // DO NOT SET organizationId - quote always belongs to the creating user's organization
      notes: data.quote?.notes
    };

    // Handle sumInsured and hectaresAnimalCount for individual vs master policies
    if (data.quote?.isMasterPolicy === false) {
      // Individual policy: values come from the single POLICYHOLDER participant
      const policyholder = data.quote.participants?.find(p => p.role === 'POLICYHOLDER');
      if (policyholder?.sumInsured !== undefined && policyholder.sumInsured > 0) {
        updateData.sumInsured = Number(policyholder.sumInsured);
      }
      if (policyholder?.hectaresAnimalCount !== undefined && policyholder.hectaresAnimalCount > 0) {
        updateData.hectaresAnimalCount = Math.floor(Number(policyholder.hectaresAnimalCount));
      }
    } else if (data.quote?.sumInsured !== undefined && data.quote.sumInsured > 0) {
      // Master policy: use direct values if set
      updateData.sumInsured = Number(data.quote.sumInsured);
      if (data.quote?.hectaresAnimalCount !== undefined && data.quote.hectaresAnimalCount > 0) {
        updateData.hectaresAnimalCount = Math.floor(Number(data.quote.hectaresAnimalCount));
      }
    } else if (data.quote?.participants?.length) {
      // Master policy fallback: calculate from all participants
      const totalSumInsured = data.quote.participants.reduce((total, p) => total + (Number(p.sumInsured) || 0), 0);
      if (totalSumInsured > 0) {
        updateData.sumInsured = Number(totalSumInsured); // Ensure it's a number, not string
      }
      const totalHectaresAnimalCount = data.quote.participants.reduce((total, p) => total + (Number(p.hectaresAnimalCount) || 0), 0);
      if (totalHectaresAnimalCount > 0) {
        updateData.hectaresAnimalCount = Math.floor(totalHectaresAnimalCount); // Ensure it's an integer
      }
    }

    // CRITICAL: Only update participants if we're in Step 2 (participants step)
    // This prevents auto-save from destroying bulk upload data during navigation
    // Note: Step detection logic may need to be updated since currentWizardStep was removed
    const isInParticipantsStep = true; // TODO: Implement proper step detection

    // Add proof of insurable interest (from Step 2)
    if (data.proofOfInterestConfirmed !== undefined || data.proofOfInterestDocuments) {
      updateData.proofOfInterest = {
        data: {
          confirmed: data.proofOfInterestConfirmed || false,
          documents: data.proofOfInterestDocuments || []
        },
        schemaId: 'quotes.proofOfInterest',
        schemaVersion: '1.0.0'
      };
    }

    // Add dates from quote object (single source of truth)
    if (data.quote?.coverageStartDate) {
      const startDate: Date | string = data.quote.coverageStartDate;
      if (typeof startDate === 'string') {
        updateData.coverageStartDate = (startDate as string).includes('T') ? startDate : `${startDate}T00:00:00.000Z`;
      } else {
        updateData.coverageStartDate = (startDate as Date).toISOString();
      }
    }
    if (data.quote?.coverageEndDate) {
      const endDate: Date | string = data.quote.coverageEndDate;
      if (typeof endDate === 'string') {
        updateData.coverageEndDate = (endDate as string).includes('T') ? endDate : `${endDate}T23:59:59.999Z`;
      } else {
        updateData.coverageEndDate = (endDate as Date).toISOString();
      }
    }

    // Add underlying assets from quote object (single source of truth)
    if (data.quote?.underlying_assets) {
      updateData.underlyingAssets = {
        data: data.quote.underlying_assets,
        schemaId: 'quotes.underlyingAssets',
        schemaVersion: '1.0.0'
      };
      // Note: Underlying assets saving correctly
    }

    // Add coverage items from quote object (single source of truth)
    if (data.quote?.coverageItems) {
      updateData.coverageItems = {
        data: data.quote.coverageItems,
        schemaId: 'quotes.coverageItems',
        schemaVersion: '1.0.0'
      };
    }

    // Add perils config from quote object (single source of truth)
    if (data.quote?.perils_config) {
      updateData.perilsConfig = {
        data: data.quote.perils_config,
        schemaId: 'quotes.perilsConfig',
        schemaVersion: '1.0.0'
      };
    }

    // CRITICAL: Only update participants if we're in Step 2 (participants step)
    // This prevents auto-save from destroying bulk upload data during navigation
    if (isInParticipantsStep) {
      
      // Add participants if present
      const participants: Array<{ farmerId?: string; organizationId?: string; role: 'POLICYHOLDER' | 'INSURED'; sumInsured?: number; hectaresAnimalCount?: number }> = [];
      
      
      // Use quote.participants directly (single source of truth)
      if (data.quote?.participants?.length) {
        data.quote.participants.forEach(participant => {
          const participantData: any = {
            role: participant.role,
          };
          
          if (participant.farmerId) {
            participantData.farmerId = participant.farmerId;
          }
          if (participant.organizationId) {
            participantData.organizationId = participant.organizationId;
          }
          if (participant.sumInsured !== undefined && participant.sumInsured !== null) {
            participantData.sumInsured = Number(participant.sumInsured);
          }
          if (participant.hectaresAnimalCount !== undefined && participant.hectaresAnimalCount !== null) {
            participantData.hectaresAnimalCount = Math.floor(Number(participant.hectaresAnimalCount));
          }
          
          participants.push(participantData);
        });
        
      }

      if (participants.length > 0) {
        updateData.participants = participants;
      }
    } else {
      // DO NOT send participants array - this preserves existing database participants
    }

    // Add location validation from quote object (single source of truth)
    if (data.quote?.locationValidation) {
      updateData.locationValidation = {
        data: data.quote.locationValidation,
        schemaId: 'quotes.locationValidation',
        schemaVersion: '1.0.0'
      };
    }

    // Add protection levels from quote object (single source of truth)
    if (data.quote?.protectionLevels) {
      updateData.protectionLevels = {
        data: data.quote.protectionLevels,
        schemaId: 'quotes.protectionLevels',
        schemaVersion: '1.0.0'
      };
    }

    // Add quotation job ID from quote object (single source of truth)
    if (data.quote?.quotationJobId) {
      updateData.quotationJobId = data.quote.quotationJobId;
    }

    // Add quotation results from quote object (single source of truth)
    if (data.quote?.quotationResults) {
      updateData.quotationResults = {
        data: data.quote.quotationResults,
        schemaId: 'quotes.quotationResults',
        schemaVersion: '1.0.0'
      };
    }

    // calculationResults removed - quotationResults is single source of truth

    // Add selected scenarios from quote object (single source of truth)
    if (data.quote?.selectedScenarios) {
      updateData.selectedScenarios = {
        data: data.quote.selectedScenarios,
        schemaId: 'quotes.selectedScenarios',
        schemaVersion: '1.0.0'
      };
    }

    // Add pricing calculation if present
    if (data.pricingCalculation) {
      updateData.premiumAmount = data.pricingCalculation.totalPremium;
      // Store pricing breakdown in notes or custom field
      updateData.pricingBreakdown = data.pricingCalculation.breakdown;
    }
    
    // Add current wizard step for restoration on reload
    if (data.currentWizardStep) {
      updateData.currentWizardStep = data.currentWizardStep;
    }

    return updateData as UpdateQuoteRequest;
  }

  /**
   * Convert wizard data to quote create format (for new quotes)
   */
  private convertWizardDataToQuoteCreate(data: QuoteWizardData): CreateDraftQuoteRequest {

    const createData: Record<string, unknown> = {
      templateName: data.quote?.templateName || 'New Draft Quote',
      productId: data.quote?.productId,
      status: 'DRAFT' as const,
      isMasterPolicy: data.quote?.isMasterPolicy || false,
      notes: data.quote?.notes,
      currentWizardStep: data.currentWizardStep || null
    };

    // Add dates from quote object (single source of truth)
    if (data.quote?.coverageStartDate) {
      const startDate: Date | string = data.quote.coverageStartDate;
      if (typeof startDate === 'string') {
        createData.coverageStartDate = (startDate as string).includes('T') ? startDate : `${startDate}T00:00:00.000Z`;
      } else {
        createData.coverageStartDate = (startDate as Date).toISOString();
      }
    }
    if (data.quote?.coverageEndDate) {
      const endDate: Date | string = data.quote.coverageEndDate;
      if (typeof endDate === 'string') {
        createData.coverageEndDate = (endDate as string).includes('T') ? endDate : `${endDate}T23:59:59.999Z`;
      } else {
        createData.coverageEndDate = (endDate as Date).toISOString();
      }
    }

    // Calculate sum insured from participants (single source of truth)
    if (data.quote?.participants?.length) {
      const totalSumInsured = data.quote.participants.reduce((total, p) => total + (Number(p.sumInsured) || 0), 0);
      if (totalSumInsured > 0) {
        createData.sumInsured = Number(totalSumInsured); // Ensure it's a number, not string
      }
      const totalHectaresAnimalCount = data.quote.participants.reduce((total, p) => total + (Number(p.hectaresAnimalCount) || 0), 0);
      if (totalHectaresAnimalCount > 0) {
        createData.hectaresAnimalCount = Math.floor(totalHectaresAnimalCount); // Ensure it's an integer
      }
    }

    // Add underlying activities for draft creation (backend expects simple arrays)
    if (data.quote?.underlying_assets?.activities_products && data.quote.underlying_assets.activities_products.length > 0) {
      createData.underlying_activities = data.quote.underlying_assets.activities_products.map((item: { type: string }) => item.type);
    }

    // Add coverage items for draft creation (backend expects simple array of strings)
    if (data.quote?.coverageItems && data.quote.coverageItems.length > 0) {
      createData.coverage_items = data.quote.coverageItems
        .filter((item: { selected: boolean }) => item.selected)
        .map((item: { type: string }) => item.type);
    }

    // Note: perilsConfig is NOT supported in draft creation
    // It will be added later via update operations when user configures perils


    // Add participants if present
    const participants: Array<{ farmerId?: string; organizationId?: string; role: 'POLICYHOLDER' | 'INSURED'; sumInsured?: number }> = [];
    
    // Use quote.participants directly (single source of truth)
    if (data.quote?.participants?.length) {
      data.quote.participants.forEach(participant => {
        const participantData: any = {
          role: participant.role,
        };
        
        if (participant.farmerId) {
          participantData.farmerId = participant.farmerId;
        }
        if (participant.organizationId) {
          participantData.organizationId = participant.organizationId;
        }
        if (participant.sumInsured !== undefined && participant.sumInsured !== null) {
          participantData.sumInsured = Number(participant.sumInsured);
        }
        
        participants.push(participantData);
      });
      
    }

    if (participants.length > 0) {
      createData.participants = participants;
    }

    // Add location validation from quote object (single source of truth)
    if (data.quote?.locationValidation) {
      createData.locationValidation = data.quote.locationValidation;
    }

    // Add protection levels from quote object (single source of truth)
    if (data.quote?.protectionLevels) {
      createData.protectionLevels = data.quote.protectionLevels;
    }

    // Add quotation job ID from quote object (single source of truth)
    if (data.quote?.quotationJobId) {
      createData.quotationJobId = data.quote.quotationJobId;
    }

    // Add quotation results from quote object (single source of truth)
    if (data.quote?.quotationResults) {
      createData.quotationResults = data.quote.quotationResults;
    }

    // Add pricing calculation if present
    if (data.pricingCalculation) {
      createData.premiumAmount = data.pricingCalculation.totalPremium;
      // Store pricing breakdown in notes or custom field
      createData.pricingBreakdown = data.pricingCalculation.breakdown;
    }
    
    // Add current wizard step for restoration on reload
    if (data.currentWizardStep) {
      createData.currentWizardStep = data.currentWizardStep;
    }

    return createData as unknown as CreateDraftQuoteRequest;
  }

  /**
   * Convert quote back to wizard data format (for loading drafts)
   */
  private convertQuoteToWizardData(quote: any): QuoteWizardData {
    return {
      // Single source of truth - all data now comes from quote object
      quote: quote,
      
      // Only keep essential wizard-specific fields that aren't in quote object
      pricingCalculation: quote.premiumAmount ? {
        totalPremium: quote.premiumAmount,
        breakdown: quote.pricingBreakdown || {
          anrp: 0,
          loadings: 0,
          fees: 0,
          taxes: 0,
          subsidies: 0,
        },
      } : undefined,
      // Note: Removed templateName, notes - now in quote object only
    };
  }
}
